import { useCallback, useEffect, useState } from "react"
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material"

import { clientApi, projectApi, taskApi } from "../api"
import { useTimer } from "../context/TimerContext"
import { usePermissions } from "../permissions"
import ClientSelect from "../components/ClientSelect"
import ProjectSelect from "../components/ProjectSelect"
import SearchSelect from "../components/SearchSelect"

/**
 * Starting a timer from anywhere.
 *
 * ── Why this exists when tasks already have a start button ──────────────────
 *
 * The board's button answers "time this task". A great deal of what a day
 * actually contains has no task on the board yet — a call, a review, half an
 * hour on a client's question — and the only way to record it was to create a
 * task for it first. People do not do that; they log it later from memory, or
 * they do not log it at all.
 *
 * ── Why a client is enough ──────────────────────────────────────────────────
 *
 * The server needs to know whose time this is, and nothing more. Project and
 * task narrow it when they apply, and both are optional, because insisting on
 * a project is what sends somebody off to create a placeholder one.
 */
export default function StartTimerDialog({ open, onClose }) {
  const { start, busy } = useTimer()
  const { isBackOffice, can } = usePermissions()

  const [clientId, setClientId] = useState("")
  const [projectId, setProjectId] = useState("")
  const [taskId, setTaskId] = useState("")
  const [billable, setBillable] = useState("task")
  const [notes, setNotes] = useState("")
  const [clients, setClients] = useState([])
  const [projects, setProjects] = useState([])
  const [tasks, setTasks] = useState([])
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open) return
    setClientId("")
    setProjectId("")
    setTaskId("")
    setBillable("task")
    setNotes("")
    setError(null)
  }, [open])

  useEffect(() => {
    if (!open) return
    projectApi
      .list({ limit: 200 })
      .then((data) => setProjects(data.projects || []))
      .catch(() => setProjects([]))
    if (isBackOffice && can("clients", "view")) {
      clientApi
        .list()
        .then((data) => setClients(data.organizations || []))
        .catch(() => setClients([]))
    }
  }, [open, isBackOffice, can])

  /*
   * The task list follows the client and project above it.
   *
   * Unfiltered it returns the first fifty tasks on the platform, which for a
   * back-office account is fifty of somebody else's. Narrowing first is what
   * makes the box usable rather than a lottery.
   */
  const loadTasks = useCallback(
    (query) => {
      if (!open) return
      const params = { limit: 50, open: "true" }
      if (clientId) params.client_id = clientId
      if (projectId) params.project_id = projectId
      if (query) params.q = query
      taskApi
        .list(params)
        .then((data) => setTasks(data.tasks || []))
        .catch(() => setTasks([]))
    },
    [open, clientId, projectId],
  )

  /* No effect of its own: the Task box calls `loadTasks` on mount and whenever
     `searchKey` changes, so a second loader here would be a duplicate request
     on every open and on every project change. */

  const submit = async (event) => {
    event.preventDefault()
    setError(null)
    try {
      await start({
        client_id: clientId || undefined,
        project_id: projectId || undefined,
        task_id: taskId || undefined,
        description: notes.trim() || undefined,
        /* Omitted when it is left on "as the task says", so the server keeps
           using the task's own answer rather than being told false. */
        ...(billable === "task" ? {} : { is_billable: billable === "billable" }),
      })
      onClose()
    } catch (err) {
      setError(err?.userMessage || "Could not start the timer")
    }
  }

  const ready = Boolean(clientId || projectId || taskId)

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle>
          Track time
          <Typography variant="caption" color="text.secondary" display="block">
            Pick a client, or go straight to the project or task it belongs to.
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Grid container spacing={2}>
            {isBackOffice && clients.length > 0 ? (
              <Grid size={{ xs: 12, sm: 6 }}>
                <ClientSelect
                  clients={clients}
                  value={clientId}
                  onChange={(id) => {
                    setClientId(id)
                    // Both belonged to the client that was there before.
                    setProjectId("")
                    setTaskId("")
                  }}
                  fullWidth
                />
              </Grid>
            ) : null}

            <Grid size={{ xs: 12, sm: isBackOffice && clients.length > 0 ? 6 : 12 }}>
              <ProjectSelect
                projects={
                  clientId ? projects.filter((project) => String(project.client_id) === String(clientId)) : projects
                }
                value={projectId}
                onChange={(id) => {
                  setProjectId(id)
                  setTaskId("")
                }}
                emptyLabel="No project"
                fullWidth
              />
            </Grid>

            <Grid size={12}>
              <SearchSelect
                options={tasks}
                value={taskId}
                onChange={setTaskId}
                label="Task"
                emptyLabel="No task"
                getLabel={(task) => (task?.task_code ? `${task.task_code}: ${task.title}` : task?.title || "")}
                getCaption={(task) => task?.project_name || task?.client_label || null}
                getSearchText={(task) => `${task.task_code ?? ""} ${task.project_name ?? ""}`}
                onSearch={loadTasks}
                searchKey={`${clientId}:${projectId}`}
                noOptionsText="No task matches"
                fullWidth
                helperText="Optional — time can belong to a client without a task"
              />
            </Grid>

            <Grid size={12}>
              {/*
                * Three options, not two.
                *
                * The task already knows whether it is billable, and that is the
                * right answer nearly every time. A two-way toggle would force
                * somebody to restate it on every timer and get it wrong
                * occasionally; "as the task says" is the default that needs no
                * thought, and the override is there for the hour spent fixing
                * our own mistake.
                */}
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Billable
              </Typography>
              <RadioGroup row value={billable} onChange={(event) => setBillable(event.target.value)}>
                <FormControlLabel value="task" control={<Radio size="small" />} label="As the task says" />
                <FormControlLabel value="billable" control={<Radio size="small" />} label="Billable" />
                <FormControlLabel value="non_billable" control={<Radio size="small" />} label="Non-billable" />
              </RadioGroup>
            </Grid>

            <Grid size={12}>
              <TextField
                label="Notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                fullWidth
                multiline
                rows={2}
                helperText="What you are about to work on. It becomes the entry's description."
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={busy} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={busy || !ready}>
            {busy ? "Starting…" : "Start timer"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
